/**
 * Formulario para crear/editar roles
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState, useEffect } from 'react';
import { usePermissionSelection } from '../../hooks';

const RoleForm = ({ role, mode = 'create', permissions = [], onSubmit, onCancel }) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    color: '#7B2D8E', // Color por defecto
    nivel_acceso: 2, // Nivel por defecto (Intermedio)
    is_active: true
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Hook para manejo de permisos
  const {
    selectedPermissions,
    togglePermission,
    selectAll,
    deselectAll,
    isPermissionSelected,
    setPermissions,
    clearSelection
  } = usePermissionSelection([], permissions);

  // Agrupar permisos por módulo (cada módulo con la LISTA de sus permisos)
  const organizePermissionsByModule = () => {
    const modules = {};

    permissions.forEach(permission => {
      const moduleKey = normalizeModuleName(permission.module || 'sistema');

      if (!modules[moduleKey]) {
        modules[moduleKey] = {
          name: moduleKey,
          displayName: getModuleDisplayName(moduleKey),
          permissions: []
        };
      }

      modules[moduleKey].permissions.push(permission);
    });

    // Ordenar los permisos dentro de cada módulo (acciones comunes primero)
    Object.values(modules).forEach(mod => {
      mod.permissions.sort((a, b) => {
        const rankA = getActionRank(a.name);
        const rankB = getActionRank(b.name);
        if (rankA !== rankB) return rankA - rankB;
        return (a.display_name || a.name).localeCompare(b.display_name || b.name);
      });
    });

    return modules;
  };

  // Orden de módulos del sistema RBAC
  const getOrderedModules = (modulesObj) => {
    const moduleOrder = [
      'dashboard', 'clientes', 'productos', 'ventas', 'inventario',
      'proveedores', 'produccion', 'vendedores', 'ordenes', 'reportes',
      'seguimiento', 'admin'
    ];

    const ordered = [];
    const entries = Object.entries(modulesObj);

    moduleOrder.forEach(moduleName => {
      const entry = entries.find(([key]) => key.toLowerCase() === moduleName);
      if (entry) ordered.push(entry);
    });

    // Cualquier módulo adicional que no esté en el orden, al final
    entries.forEach(entry => {
      if (!moduleOrder.includes(entry[0].toLowerCase())) ordered.push(entry);
    });

    return ordered;
  };

  const permissionsByModule = organizePermissionsByModule();
  const orderedModules = getOrderedModules(permissionsByModule);

  // Inicializar formulario con datos del rol si es edición
  useEffect(() => {
    if (role && mode === 'edit') {
      setFormData({
        name: role.name || '',
        display_name: role.display_name || '',
        description: role.description || '',
        color: role.color || '#7B2D8E',
        nivel_acceso: role.nivel_acceso || role.level || 2,
        is_active: role.is_active !== undefined ? role.is_active : true
      });

      // Establecer permisos seleccionados (ids)
      const rolePermissionIds = role.permissions ? role.permissions.map(p => p.id || p) : [];
      setPermissions(rolePermissionIds);
    } else {
      clearSelection();
    }
  }, [role, mode, setPermissions, clearSelection]);

  // Manejador de cambios en campos
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Generar nombre interno desde display_name
  const generateInternalName = (displayName) => {
    return displayName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .trim();
  };

  const handleDisplayNameChange = (e) => {
    const displayName = e.target.value;
    setFormData(prev => ({
      ...prev,
      display_name: displayName,
      name: mode === 'create' ? generateInternalName(displayName) : prev.name
    }));

    if (errors.display_name) {
      setErrors(prev => ({ ...prev, display_name: null }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'El nombre solo puede contener letras minúsculas, números y guiones bajos';
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'El nombre para mostrar es requerido';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const roleData = {
        ...formData,
        permissions: selectedPermissions
      };

      const result = await onSubmit(roleData);

      if (result && !result.success) {
        if (result.validationErrors) {
          setErrors(result.validationErrors);
        } else {
          setErrors({ general: result.message });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ general: 'Error procesando formulario' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="role-form">
      <form onSubmit={handleSubmit}>
        {/* Error general */}
        {errors.general && (
          <div className="alert alert-error mb-4">
            <i className="fas fa-exclamation-triangle"></i>
            {errors.general}
          </div>
        )}

        <div className="form-sections">
          {/* Información Básica */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-info-circle"></i>
              Información Básica
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="display_name" className="required">Nombre para Mostrar</label>
                <input
                  type="text"
                  id="display_name"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleDisplayNameChange}
                  className={`form-control ${errors.display_name ? 'is-invalid' : ''}`}
                  placeholder="Ej: Administrador, Vendedor, Supervisor"
                />
                {errors.display_name && (
                  <div className="invalid-feedback">{errors.display_name}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="name" className="required">Nombre Interno</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  placeholder="administrador, vendedor, supervisor"
                  readOnly={mode === 'edit'}
                />
                <small className="form-text text-muted">
                  {mode === 'edit'
                    ? 'El nombre interno no se puede cambiar después de crear el rol'
                    : 'Se genera automáticamente. Solo letras minúsculas, números y guiones bajos'
                  }
                </small>
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description" className="required">Descripción</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                rows="3"
                placeholder="Describe las responsabilidades y funciones de este rol"
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>
          </div>

          {/* Estado del Rol */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-cogs"></i>
              Estado
            </h3>

            <div className="form-group">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label htmlFor="is_active" className="form-check-label">
                  Rol activo
                  <small className="help-text">Los roles inactivos no se pueden asignar a usuarios</small>
                </label>
              </div>
            </div>
          </div>

          {/* Permisos */}
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-shield-alt"></i>
              Permisos del Rol
              <div className="section-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success"
                  onClick={selectAll}
                >
                  Seleccionar Todos
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  onClick={deselectAll}
                >
                  Deseleccionar Todos
                </button>
              </div>
            </h3>

            <div className="permissions-container">
              <div className="permissions-summary" style={{ marginBottom: '16px' }}>
                <span className="selected-count">
                  {selectedPermissions.length} de {permissions.length} permisos seleccionados
                </span>
              </div>

              {permissions.length === 0 ? (
                <div style={{
                  background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px',
                  padding: '16px', color: '#856404'
                }}>
                  No hay permisos disponibles en el sistema.
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '16px'
                }}>
                  {orderedModules.map(([moduleKey, moduleData]) => {
                    const moduleIds = moduleData.permissions.map(p => p.id);
                    const selectedInModule = moduleIds.filter(id => isPermissionSelected(id)).length;
                    const allSelected = moduleIds.length > 0 && selectedInModule === moduleIds.length;

                    const toggleModule = () => {
                      if (allSelected) {
                        setPermissions(selectedPermissions.filter(id => !moduleIds.includes(id)));
                      } else {
                        setPermissions([...new Set([...selectedPermissions, ...moduleIds])]);
                      }
                    };

                    return (
                      <div key={moduleKey} style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                      }}>
                        {/* Cabecera del módulo */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          padding: '12px 14px',
                          background: '#faf5fc',
                          borderBottom: '1px solid #f0e6f5'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#111827' }}>
                            <i className={`fas fa-${getModuleIcon(moduleKey)}`} style={{ color: '#7B2D8E' }}></i>
                            {moduleData.displayName}
                            <span style={{
                              fontSize: '11px', fontWeight: 600, color: '#7B2D8E',
                              background: '#f3e8f7', borderRadius: '10px', padding: '2px 8px'
                            }}>
                              {selectedInModule}/{moduleIds.length}
                            </span>
                          </div>
                          <label style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '12px', color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={el => { if (el) el.indeterminate = selectedInModule > 0 && !allSelected; }}
                              onChange={toggleModule}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#7B2D8E' }}
                            />
                            Todos
                          </label>
                        </div>

                        {/* Lista de permisos del módulo */}
                        <div style={{ padding: '8px 14px 12px' }}>
                          {moduleData.permissions.map(permission => (
                            <label
                              key={permission.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '7px 0', cursor: 'pointer', fontSize: '14px', color: '#374151'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isPermissionSelected(permission.id)}
                                onChange={() => togglePermission(permission.id)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7B2D8E', flexShrink: 0 }}
                              />
                              <span>{permission.display_name || permission.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {mode === 'create' ? 'Creando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <i className={`fas ${mode === 'create' ? 'fa-plus' : 'fa-save'}`}></i>
                {mode === 'create' ? 'Crear Rol' : 'Guardar Cambios'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Normalizar nombre de módulo (acepta el enum en MAYÚSCULAS del backend y alias)
const normalizeModuleName = (moduleName) => {
  const normalized = (moduleName || 'sistema').toLowerCase().trim();

  const moduleMap = {
    dashboard: 'dashboard',
    clientes: 'clientes', clients: 'clientes', cliente: 'clientes',
    productos: 'productos', products: 'productos', producto: 'productos',
    ventas: 'ventas', sales: 'ventas', venta: 'ventas',
    inventario: 'inventario', inventory: 'inventario',
    proveedores: 'proveedores', suppliers: 'proveedores', proveedor: 'proveedores',
    produccion: 'produccion', production: 'produccion',
    vendedores: 'vendedores', sellers: 'vendedores', vendedor: 'vendedores',
    ordenes: 'ordenes', orders: 'ordenes', orden: 'ordenes',
    reportes: 'reportes', reports: 'reportes', reporte: 'reportes',
    seguimiento: 'seguimiento', tracking: 'seguimiento',
    admin: 'admin', administration: 'admin', users: 'admin', usuarios: 'admin'
  };

  return moduleMap[normalized] || normalized;
};

// Rango de orden para acciones comunes (a partir del segundo segmento del nombre)
const getActionRank = (permissionName) => {
  const parts = (permissionName || '').split('.');
  const action = (parts.length >= 2 ? parts[1] : '').toLowerCase();
  const order = ['view', 'create', 'update', 'delete'];
  const idx = order.indexOf(action);
  return idx === -1 ? order.length : idx;
};

// Nombre en español de módulos
const getModuleDisplayName = (module) => {
  const displayNames = {
    dashboard: 'Dashboard',
    clientes: 'Clientes',
    productos: 'Productos',
    ventas: 'Ventas',
    inventario: 'Inventario',
    proveedores: 'Proveedores',
    produccion: 'Producción',
    vendedores: 'Vendedores',
    ordenes: 'Órdenes',
    reportes: 'Reportes',
    seguimiento: 'Seguimiento',
    admin: 'Administración',
    usuarios: 'Usuarios',
    roles: 'Roles',
    permisos: 'Permisos',
    compras: 'Compras',
    facturacion: 'Facturación',
    prospeccion: 'Prospección',
    sistema: 'Sistema'
  };

  const normalized = module.toLowerCase().trim();
  return displayNames[normalized] || module.charAt(0).toUpperCase() + module.slice(1);
};

// Iconos de módulos (Font Awesome)
const getModuleIcon = (module) => {
  const icons = {
    dashboard: 'tachometer-alt',
    clientes: 'users',
    productos: 'box',
    ventas: 'shopping-cart',
    inventario: 'warehouse',
    proveedores: 'truck',
    produccion: 'industry',
    vendedores: 'user-tie',
    ordenes: 'file-alt',
    reportes: 'chart-bar',
    seguimiento: 'search',
    admin: 'cogs',
    usuarios: 'user',
    roles: 'user-shield',
    permisos: 'key',
    compras: 'shopping-bag',
    facturacion: 'receipt',
    prospeccion: 'search-plus',
    sistema: 'cog'
  };

  const normalized = module.toLowerCase().trim();
  return icons[normalized] || 'folder';
};

export default RoleForm;
